import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningIcon from '@mui/icons-material/Warning';
import queryString from 'query-string';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { CreateScanRequest } from '../../common/model/dto/create-scan-request';
import { Resource } from '../../common/model/type/resource.enum';
import { ListSortOption } from '../../common/model/ui/list-sort-option';
import { SCAN_LIST_FILTERS } from '../../common/model/ui/scan-list-filters';
import { OrderBy } from '../../common/types';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import DateUtils from '../../common/utils/services/date-utils';
import { isFilterApplied } from '../../common/utils/services/list-filter-utils';
import { getListRange } from '../../common/utils/services/order-list-utils';
import { ScanFields } from '../../firebase/document-field.enums';
import { useAdminScanMutations } from '../../hooks/use-admin-scan-mutations';
import useAuthorized from '../../hooks/use-authorized';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import { useScanListFilters } from '../../hooks/use-scan-list-filters';
import { useAppSelector } from '../../redux/hooks';
import { ScansService } from '../../services/scans.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvSearchInput from '../UI/atoms/OvSearchInput';
import OvCreateScanDialog from '../UI/molecules/OvCreateScanDialog';
import OvListSortSelect from '../UI/molecules/OvListSortSelect';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvAdminScanList from '../UI/organisms/OvAdminScanList';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';

const SCAN_LIST_LIMIT = 10;
const DEFAULT_SCAN_LIST_ORDER: OrderBy = {
  order_by: 'created_at',
  order_dir: 'desc',
};
const RANGE_FILTER_FIELDS: string[] = [
  ScanFields.lh,
  ScanFields.pg,
  ScanFields.lr_e3g,
];

const getScanSortOptions = (
  translate: (key: string) => string
): ListSortOption[] => [
  {
    order_by: 'created_at',
    order_dir: 'desc',
    label: translate('scanList.sortOptions.createdNewest'),
  },
  {
    order_by: 'created_at',
    order_dir: 'asc',
    label: translate('scanList.sortOptions.createdOldest'),
  },
];

const toScanQueryFilters = (params: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(params).map(([field, value]) => [
      field,
      RANGE_FILTER_FIELDS.includes(field) ? value.split(',') : value,
    ])
  );

const AdminScanList: FC = () => {
  const { t } = useTranslation();
  const [isCreateScanDialogOpen, setIsCreateScanDialogOpen] = useState(false);
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const applicationOperations = useAuthorized(Resource.Application);
  const scanOperations = useAuthorized(Resource.Scan);
  const scanCreateAllowed =
    scanOperations.create || applicationOperations.supervise;

  const {
    offset,
    orderBy,
    filters,
    filterQueryParams,
    changeFilter,
    clearFilters,
    changeOrderBy,
    setOffset,
  } = useListQueryParams(SCAN_LIST_FILTERS, DEFAULT_SCAN_LIST_ORDER);
  const filtersWithOptions = useScanListFilters(filters);
  const [searchText, setSearchText] = useQueryParam(
    'search_text',
    withDefault(StringParam, '')
  );

  const query = queryString.stringify(
    {
      limit: SCAN_LIST_LIMIT,
      offset,
      order_by: orderBy?.order_by,
      order_dir: orderBy?.order_dir,
      ...toScanQueryFilters(filterQueryParams),
      search_text: searchText
        ? searchText.toLowerCase().split(' ').join(',')
        : undefined,
    },
    { arrayFormat: 'comma' }
  );

  const {
    data: paginatedScans,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-scans', query],
    queryFn: () => ScansService.loadScans(query),
    retry: 3,
    placeholderData: keepPreviousData,
  });

  const scans = paginatedScans?.docs ?? [];
  const total = paginatedScans?.total ?? 0;
  const hasNarrowedList = filters.some(isFilterApplied) || !!searchText;

  const { deleteScan, createScan } = useAdminScanMutations();

  const onSaveScan = (request: CreateScanRequest) => {
    setIsCreateScanDialogOpen(false);
    createScan.mutate(request);
  };

  const onSearchChange = (value: string) => {
    setOffset(0);
    setSearchText(value.toLowerCase() || null);
  };

  const onClear = () => {
    clearFilters();
    setSearchText(null);
  };

  const getResultSummary = () => {
    if (isError) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('common.listResults.loading');
    }

    return t('scanList.resultRange', {
      ...getListRange(offset, SCAN_LIST_LIMIT, total),
      total,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('scanList.states.errorTitle')}
          description={t('scanList.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (scans.length > 0) {
      return (
        <OvAdminScanList
          scans={scans}
          deleteScanCallback={(scanId) => deleteScan.mutate(scanId)}
        />
      );
    }

    if (hasNarrowedList) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t('scanList.states.noResultsTitle')}
          description={t('scanList.states.noResultsDescription')}
        >
          <OvCompactButton onClick={onClear}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<DocumentScannerIcon />}
        title={t('scanList.states.emptyTitle')}
        description={t('scanList.states.emptyDescription')}
      />
    );
  };

  const mutationError = createScan.error ?? deleteScan.error;

  return (
    <OvRecordListPage
      title={t('app.subPages.menu.scans')}
      icon={<DocumentScannerIcon />}
      headerAction={
        <>
          <OvSearchInput
            placeholder={t('common.actions.search')}
            initialValue={searchText}
            onSearchChange={onSearchChange}
          />
          {scanCreateAllowed && (
            <OvCompactButton
              icon={<AddCircleIcon />}
              onClick={() => setIsCreateScanDialogOpen(true)}
            >
              {t('common.actions.create')}
            </OvCompactButton>
          )}
        </>
      }
      filters={
        <OvListFilterBar
          filters={filtersWithOptions}
          onFilterChange={changeFilter}
          onClearFilters={clearFilters}
        >
          <OvListSortSelect
            options={getScanSortOptions(t)}
            orderBy={orderBy?.order_by ?? ''}
            orderDir={orderBy?.order_dir ?? ''}
            onChange={changeOrderBy}
          />
        </OvListFilterBar>
      }
      context={
        mutationError && (
          <OvErrorMessage message={getAxiosErrorMessage(mutationError)} />
        )
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={SCAN_LIST_LIMIT}
      offset={offset}
      onPageChange={(_, page) => setOffset((page - 1) * SCAN_LIST_LIMIT)}
      isBusy={isFetching || createScan.isPending || deleteScan.isPending}
    >
      {renderContent()}

      {scanCreateAllowed && (
        <OvCreateScanDialog
          isOpen={isCreateScanDialogOpen}
          isScanLoading={createScan.isPending}
          onCancel={() => setIsCreateScanDialogOpen(false)}
          onSave={onSaveScan}
          selectedUserId={currentUser.id}
          selectedDay={DateUtils.getDbDateTag(new Date())}
          showDaySelector={true}
          showUserSelector={true}
          showImageUploader={true}
        />
      )}
    </OvRecordListPage>
  );
};

export default AdminScanList;
